import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Rating } from 'react-simple-star-rating';
import { Heart, Trash2, ShoppingCart, ChevronRight, Package, AlertCircle, Loader2, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToggleWishlist, setWishlistItems } from '../../redux/wishlistSlice';
import { setCart } from '../../redux/cartSlice';
import toast from 'react-hot-toast';
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';
import { removeItem } from '../../redux/hejalSlice';

const WishList = () => {
    const [wishlistData, setWishlistData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [removing, setRemoving] = useState({});
    const [addingToCart, setAddingToCart] = useState({});
    const cartItems = useSelector((state) => state.hejal.cartItems);

    const isLoggedIn = useSelector((state) => state.hejal.isLoggedIn);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Fetch wishlist data from API
    const fetchWishlist = async (page = 1) => {
        if (!isLoggedIn) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const response = await ApiService({
                method: "GET",
                endpoint: `${apiPaths.wishlist}?page=${page}&limit=12`,
            });

            if (response?.success && response?.data) {
                setWishlistData(response.data.wishlist || []);
                setPagination(response.data.pagination || {});
                
                // Update Redux store for other components
                dispatch(setWishlistItems(response.data.wishlist || []));
            } else {
                setWishlistData([]);
                toast.error(response?.message || "Failed to fetch wishlist");
            }
        } catch (error) {
            console.error("Error fetching wishlist:", error);
            setWishlistData([]);
            toast.error(error?.message || "Failed to fetch wishlist");
        } finally {
            setLoading(false);
        }
    };

    // Add this function to find cart item (similar to your cartContainsProduct)
const findCartItem = (item) => {
    if (!Array.isArray(cartItems)) return null;
    
    const productId = item.product?._id;
    const variantSku = item.variantSku || item.selectedVariant?.sku;
    
    return cartItems.find(cartItem => {
        const cartProductId = cartItem.product?._id || cartItem.productId;
        const cartVariantSku = cartItem.variantSku;
        
        if (variantSku && cartVariantSku) {
            return cartProductId === productId && cartVariantSku === variantSku;
        }
        if (!variantSku && !cartVariantSku) {
            return cartProductId === productId;
        }
        return false;
    });
};

// Updated removeFromCart function
const removeFromCart = async (item) => {
    const cartItem = findCartItem(item);
    if (!cartItem) return;

    const productId = item.product?._id;
    setAddingToCart(prev => ({ ...prev, [productId]: true }));
    
    try {
        const response = await ApiService({
            method: "DELETE",
            endpoint: `${apiPaths.removeFromCart}/${cartItem._id}`,
        });

        if (response.success) {
            dispatch(removeItem({ cartItemId: cartItem._id }));
            toast.success("Product removed from cart");
        } else {
            toast.error("Failed to remove product");
        }
    } catch (err) {
        toast.error("Failed to remove product");
        console.error("Error removing from cart:", err);
    } finally {
        setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
};

// Check if product is in cart
const isProductInCart = (item) => {
    if (!Array.isArray(cartItems)) return false;
    
    const productId = item.product?._id;
    const variantSku = item.variantSku || item.selectedVariant?.sku;
    
    console.log("Looking for productId:", productId, "variantSku:", variantSku);
    console.log("Cart structure:", cartItems[0]); // Log first cart item structure
    
    return cartItems.some(cartItem => {
        const cartProductId = cartItem.product?._id;
        
        console.log("Cart item:", cartItem);
        console.log("Comparing productId:", cartProductId === productId);
        
        // For now, just match by product ID since your cart doesn't seem to store variant info
        return cartProductId === productId;
    });
};
const removeFromWishlist = async (item) => {
    if (!isLoggedIn) {
        toast.error("Please Log In");
        navigate("/signin");
        return;
    }

    const productId = item.product?._id;
    if (!productId) {
        toast.error("Invalid product");
        return;
    }

    setRemoving(prev => ({ ...prev, [productId]: true }));

    try {
        const response = await ApiService({
            method: "POST",
            endpoint: apiPaths.wishlist,
            data: {
                productId: productId,
                variantSku: item.variantSku || null
            },
        });

        if (response?.success) {
            // Remove from local state immediately
            setWishlistData(prev => prev.filter(wishlistItem => wishlistItem._id !== item._id));
            
            // Update pagination count
            setPagination(prev => ({
                ...prev,
                totalItems: Math.max(0, (prev.totalItems || 0) - 1)
            }));

            toast.success("Removed from wishlist");
        } else {
            toast.error(response?.message || "Failed to remove from wishlist");
        }
    } catch (error) {
        console.error("Error removing from wishlist:", error);
        toast.error("Failed to remove from wishlist");
    } finally {
        setRemoving(prev => ({ ...prev, [productId]: false }));
    }
};

// ===================================
// 2. BUTTON CLICK HANDLER (REPLACE IN YOUR COMPONENT)
// ===================================


    useEffect(() => {
        fetchWishlist(currentPage);
    }, [isLoggedIn, currentPage]);

    const toggleFavorite = async (product) => {
        if (!isLoggedIn) {
            toast.error("Please Log In");
            navigate("/signin");
            return;
        }

        if (!product?._id) {
            toast.error("Invalid product");
            return;
        }

        const productId = product.product?._id || product._id;
        setRemoving(prev => ({ ...prev, [productId]: true }));

        try {
            // Optimistically update UI
            dispatch(ToggleWishlist(product));
            
            const response = await ApiService({
                method: "POST",
                endpoint: apiPaths.wishlist,
                data: {
                    productId: productId,
                },
            });

            if (response?.success) {
                toast.success(response.message || "Wishlist updated");
                // Refresh wishlist data
                fetchWishlist(currentPage);
            } else {
                throw new Error(response?.message || "Failed to update wishlist");
            }
        } catch (error) {
            console.error("Error updating wishlist:", error);
            // Revert optimistic update on error
            dispatch(ToggleWishlist(product));
            toast.error(error?.message || "Failed to update wishlist");
        } finally {
            setRemoving(prev => ({ ...prev, [productId]: false }));
        }
    };

    // Add to Cart function based on provided logic
    const addToCart = async (item) => {
        if (!isLoggedIn) {
            toast.error("Please Log In");
            navigate("/signin");
            return;
        }

        if (!item.selectedVariant) {
            toast.error("Please select color and size from product page");
            // Navigate to product page for variant selection
            navigate(`/product/${getProductSlug(item)}`);
            return;
        }

        if (item.selectedVariant?.inventory?.stock && item.selectedVariant.inventory.stock <= 0) {
            toast.error("This variant is currently out of stock");
            return;
        }

        const productId = getProductId(item);
        setAddingToCart(prev => ({ ...prev, [productId]: true }));

        try {
            const response = await ApiService({
                method: "POST",
                endpoint: apiPaths.addToCart,
                data: {
                    productId: productId,
                    variantSku: item.selectedVariant.sku,
                    quantity: 1 // Default quantity from wishlist
                },
            });

            if (response.success) {
                // FETCH UPDATED CART TO GET LIVE PRICES
                const cartResponse = await ApiService({
                    method: "GET",
                    endpoint: apiPaths.getCartProducts,
                });

                if (cartResponse.success && cartResponse.data) {
                    // Use the new setCart action
                    dispatch(setCart(cartResponse.data));
                }

                toast.success("Product added to cart");
            } else {
                toast.error(response.message || "Failed to add product to cart");
            }

        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to add product to cart");
            console.error("Error adding to cart:", err);
        } finally {
            setAddingToCart(prev => ({ ...prev, [productId]: false }));
        }
    };

    const getProductPrice = (product) => {
        if (!product) return { current: 0, original: 0, discount: 0 };

        // Handle new schema with pricing from variants
        if (product.pricing) {
            const current = product.pricing.salePrice || product.pricing.basePrice || product.pricing.minPrice || 0;
            const original = product.pricing.basePrice || product.pricing.maxPrice || current;
            const discount = product.pricing.discount || 0;
            
            return { current, original, discount };
        }

        // Fallback to old schema
        const current = product.discountedAmount || product.price || 0;
        const original = product.price || current;
        const discount = original && current ? Math.round(((original - current) / original) * 100) : 0;
        
        return { current, original, discount };
    };

    const getProductImage = (product) => {
        // Handle new schema
        if (product.product?.mainImage) return product.product.mainImage;
        if (product.mainImage) return product.mainImage;
        
        // Fallback to old schema
        if (product.productImages?.[0]) return product.productImages[0];
        if (product.product?.productImages?.[0]) return product.product.productImages[0];
        
        return '/placeholder-image.jpg';
    };

    const getProductName = (product) => {
        return product.product?.name || product.productName || product.name || 'Unknown Product';
    };

    const getProductId = (product) => {
        return product.product?._id || product._id;
    };

    const getProductSlug = (product) => {
        return product.product?.slug || product.slug || getProductId(product);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!isLoggedIn) {
        return (
            <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <motion.div
                        className="bg-white rounded-xl shadow-sm p-12 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <AlertCircle className="h-20 w-20 text-gray-200 mx-auto mb-6" />
                        <h2 className="text-2xl font-medium text-gray-700 mb-3">Please Log In</h2>
                        <p className="text-gray-500 mb-8">You need to be logged in to view your wishlist</p>
                        <Link
                            to="/signin"
                            className="bg-secondColor text-white font-medium px-8 py-3 rounded-lg transition-colors inline-block"
                        >
                            Sign In
                        </Link>
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-b from-gray-50 to-white min-h-screen py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Animated Header */}
                <motion.div
                    className="relative mb-12 overflow-hidden"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="absolute inset-0 bg-indigo-600 rounded-xl opacity-10" />

                    <div className="relative py-8 px-6 md:px-10">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
                            <div className="flex items-center">
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.3, duration: 0.4 }}
                                    className="bg-indigo-100 p-3 rounded-full mr-4"
                                >
                                    <Heart className="h-6 w-6 text-indigo-600" />
                                </motion.div>

                                <div>
                                    <h1 className="md:text-3xl text-xl font-bold text-gray-800 mb-1">My Wishlist</h1>
                                    <div className="flex items-center text-gray-500 md:text-xl text-sm ">
                                        <span>Home</span>
                                        <ChevronRight className="h-4 w-4 mx-2" />
                                        <span className="text-indigo-600 font-medium">Wishlist</span>
                                    </div>
                                </div>
                            </div>

                            <motion.div
                                className="mt-4 sm:mt-0 bg-white shadow-sm rounded-full px-5 py-2 flex items-center"
                                initial={{ x: 20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: 0.5, duration: 0.4 }}
                            >
                                <Package className="h-5 w-5 text-indigo-500 mr-2" />
                                <span className="text-sm font-medium text-gray-700">
                                    {loading ? "..." : (pagination.totalItems || 0)} items
                                </span>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>

                {/* Loading State */}
                {loading && (
                    <motion.div
                        className="bg-white rounded-xl shadow-sm p-12 text-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        <Loader2 className="h-12 w-12 text-indigo-500 mx-auto mb-4 animate-spin" />
                        <p className="text-gray-500">Loading your wishlist...</p>
                    </motion.div>
                )}

                {/* Empty State */}
                {!loading && wishlistData?.length === 0 && (
                    <motion.div
                        className="bg-white rounded-xl shadow-sm p-12 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <motion.div
                            className="flex justify-center mb-6"
                            initial={{ scale: 0.5 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <Heart className="h-20 w-20 text-gray-200" />
                        </motion.div>
                        <h2 className="text-2xl font-medium text-gray-700 mb-3">Your wishlist is empty</h2>
                        <p className="text-gray-500 max-w-md mx-auto mb-8">Items added to your wishlist will appear here. Find something you love and add it to your collection!</p>
                        <Link
                            to="/shop"
                            className="bg-secondColor text-white font-medium px-8 py-3 rounded-lg transition-colors inline-block hover:bg-opacity-90"
                        >
                            Start Shopping
                        </Link>
                    </motion.div>
                )}

                {/* Wishlist Items */}
                {!loading && wishlistData?.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {wishlistData.map((item, index) => {
                                const productId = getProductId(item);
                                const pricing = getProductPrice(item);
                                const productImage = getProductImage(item);
                                const productName = getProductName(item);
                                const productSlug = getProductSlug(item);
                                const isRemoving = removing[productId] || false;
                                const isItemAddingToCart = addingToCart[productId] || false;

                                return (
                                    <motion.div
                                        key={item._id || index}
                                        className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                        whileHover={{ y: -5 }}
                                    >
                                        <div className="p-5">
                                            <div className="flex flex-col sm:flex-row gap-5">
                                                {/* Product Image */}
                                                <Link 
                                                    to={`/product/${productSlug}`} 
                                                    className="flex-shrink-0 bg-gray-50 rounded-lg p-4 flex items-center justify-center"
                                                >
                                                    <motion.img
                                                        src={productImage}
                                                        alt={productName}
                                                        className="w-28 h-28 object-contain"
                                                        whileHover={{ scale: 1.05 }}
                                                        transition={{ duration: 0.3 }}
                                                        onError={(e) => {
                                                            e.target.src = '/placeholder-image.jpg';
                                                        }}
                                                    />
                                                </Link>

                                                {/* Product Details */}
                                                <div className="flex-1 flex flex-col">
                                                    <div className="mb-2">
                                                        <Link to={`/product/${productSlug}`}>
                                                            <h2 className="text-lg font-medium text-gray-800 mb-1 line-clamp-1 hover:text-indigo-600 transition-colors">
                                                                {productName}
                                                            </h2>
                                                        </Link>
                                                        {/* Product details from new schema */}
                                                        {item.product?.brand && (
                                                            <p className="text-sm text-gray-500">Brand: {item.product.brand}</p>
                                                        )}
                                                        {item.selectedVariant && (
                                                            <div className="text-sm text-gray-500">
                                                                <span>Size: {item.selectedVariant.size.name}</span>
                                                                <span className="ml-3">Color: {item.selectedVariant.color.name}</span>
                                                            </div>
                                                        )}
                                                        {item.availableSizes && item.availableSizes.length > 0 && (
                                                            <p className="text-sm text-gray-500">
                                                                Available in: {item.availableSizes.join(', ')}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Rating */}
                                                    {(item.product?.rating?.average || item.avgRating) && (
                                                        <div className="mt-1 mb-3">
                                                            <Rating
                                                                initialValue={item.product?.rating?.average || item.avgRating || 0}
                                                                readonly
                                                                allowFraction
                                                                size={18}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Pricing */}
                                                    <div className="flex items-center gap-2 mb-4">
                                                        {pricing.current > 0 && (
                                                            <>
                                                                <span className="text-xl font-bold text-gray-900">
                                                                    ₹{pricing.current.toLocaleString()}
                                                                </span>
                                                                {pricing.original > pricing.current && (
                                                                    <>
                                                                        <span className="text-sm text-gray-400 line-through">
                                                                            ₹{pricing.original.toLocaleString()}
                                                                        </span>
                                                                        <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                                                            {pricing.discount}% off
                                                                        </span>
                                                                    </>
                                                                )}
                                                                {item.pricing?.priceRange && (
                                                                    <span className="text-xs text-gray-500">
                                                                        (Price range)
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Stock Status */}
                                                    {item.isInStock !== undefined && (
                                                        <div className="mb-2">
                                                            <span className={`text-xs px-2 py-1 rounded-full ${
                                                                item.isInStock 
                                                                    ? 'bg-green-50 text-green-700' 
                                                                    : 'bg-red-50 text-red-700'
                                                            }`}>
                                                                {item.isInStock ? 'In Stock' : 'Out of Stock'}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Action Buttons */}
                                                    <div className="mt-auto flex flex-wrap gap-2">
<motion.button
   className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
   whileHover={{ scale: 1.02 }}
   whileTap={{ scale: 0.98 }}
   onClick={() => navigate(`/product/${getProductId(item)}`)}
>
   <Eye className="h-4 w-4" />
   <span>View Product</span>
</motion.button>
                                                      <motion.button
    className="p-2 text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg transition-colors relative disabled:opacity-50"
    whileHover={{ scale: 1.05, borderColor: "#f56565" }}
    whileTap={{ scale: 0.95 }}
    onClick={() => removeFromWishlist(item)}
    disabled={isRemoving}
>
    {isRemoving ? (
        <Loader2 className="h-5 w-5 animate-spin" />
    ) : (
        <Trash2 className="h-5 w-5" />
    )}
</motion.button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {pagination?.totalPages > 1 && (
                            <motion.div
                                className="flex justify-center mt-8"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                            >
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={!pagination.hasPrevPage}
                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    
                                    <span className="px-4 py-2 text-sm text-gray-700">
                                        Page {pagination.currentPage} of {pagination.totalPages}
                                    </span>
                                    
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={!pagination.hasNextPage}
                                        className="px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default WishList;